<app-dialog-header
  heading="Risk Adjustment Policy Service Year"
  subheading="Set the service year, submission rules and incoming CMS files for this payer."
  contextLabel="Payer"
  [contextValue]="payerEditStore.currentPayerItem()?.PayerName"></app-dialog-header>

<mat-dialog-content>
  @if (isCopy) {
    <div class="copy-notice" role="status">
      <mat-icon class="copy-notice__icon">content_copy</mat-icon>
      <span>
        Copied from the <strong>{{ item.ServiceYearStart }} – {{ item.ServiceYearEnd }}</strong> policy.
        Enter a new Service Year range to save the copy.
      </span>
    </div>
  }

  <app-form-section heading="Service Year" description="Date range this policy applies to.">
    <app-labeled-field label="Start Date" [required]="true">
      <mat-form-field color="primary" appearance="outline">
        <input matInput [matDatepicker]="startPicker" [formControl]="serviceYearStartControl" (dateChange)="onStartDateChange($event)" placeholder="MM/DD/YYYY" aria-label="Start Date" required>
        <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
        @if (serviceYearStartControl.hasError('required')) {
          <mat-error>Date is <strong>required</strong></mat-error>
        }
      </mat-form-field>
    </app-labeled-field>

    <app-labeled-field label="End Date" [required]="true">
      <mat-form-field color="primary" appearance="outline">
        <input matInput [matDatepicker]="endPicker" [min]="serviceYearStartControl.value" [formControl]="serviceYearEndControl" placeholder="MM/DD/YYYY" aria-label="End Date" required>
        <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
        @if (serviceYearEndControl.hasError('required')) {
          <mat-error>Date is <strong>required</strong></mat-error>
        }
        @if (serviceYearEndControl.hasError('matDatepickerMin')) {
          <mat-error>Date must be greater than start date</mat-error>
        }
      </mat-form-field>
    </app-labeled-field>

    @if (form.hasError('serviceYearOverlap')) {
      <p class="service-year-error" role="alert">
        This range overlaps the existing {{ overlappingRange }} policy for this payer.
      </p>
    }
  </app-form-section>

  <app-form-section heading="Policy" description="Whether risk adjustment applies and how omissions are handled.">
    <app-labeled-field #riskPolicyField label="Risk Adjustment Policy" [required]="true">
      <app-yes-no-toggle
        [formControl]="hasRiskAdjustmentControl"
        [ariaLabelledby]="riskPolicyField.labelId"
        (valueChanged)="onRiskPolicyChanged($event)"></app-yes-no-toggle>
    </app-labeled-field>

    <app-labeled-field #manualOmitField label="Manual Omit">
      <app-yes-no-toggle
        [formControl]="hasManualOmitControl"
        [ariaLabelledby]="manualOmitField.labelId"
        (valueChanged)="onManualOmitChanged($event)"></app-yes-no-toggle>
    </app-labeled-field>
  </app-form-section>

  <app-form-section heading="Submission & Export" description="Submission format, payer timing and the export template that goes with it.">
    <app-labeled-field label="Submission Type" [required]="true">
      <app-preferred-action-filter-select
        #riskPolicyPaSelect
        [form]="form"
        [controlName]="preferredActionControlName"
        [label]="''"
        [placeholder]="'Make a Selection'"
        [onlyShowOptions]="['99499', 'ASM']"
        [multiple]="false"
        [required]="form.get(hasRiskAdjustmentControlName)?.value == 'Yes' && form.get(hasManualOmitControlName)?.value == 'No'"
        [selectAllEnabled]="false"
        [disabled]="form.get(hasRiskAdjustmentControlName)?.value == 'No' || form.get(hasManualOmitControlName)?.value == 'Yes'"
        (valueChanged)="onPreferredActionChanged($event)"></app-preferred-action-filter-select>
    </app-labeled-field>

    <app-labeled-field label="Payer Lead Time" [required]="true" [hint]="leadTimeHint">
      <app-lead-time-filter-select
        [form]="form"
        [required]="true"
        [initialValue]="item?.SubmissionLeadTimeDays"
        [disabled]="form.get(hasRiskAdjustmentControlName)?.value == 'No'"></app-lead-time-filter-select>
    </app-labeled-field>

    @if (asmTemplateEnabled) {
      <app-labeled-field label="ASM Template">
        <app-filter-search-select
          #asmTemplate
          [form]="form"
          [controlName]="asmTemplateControlName"
          [noneEnabled]="false"
          [multiple]="true"
          [noEntriesFoundLabel]="'No Templates Found'"></app-filter-search-select>
        @if (isCopy && copiedTemplateIds.length > 0) {
          <mat-checkbox
            color="primary"
            [formControl]="copyTemplatesControl"
            (change)="onCopyTemplatesChange($event.checked)">
            Copy Templates ({{ copiedTemplateIds.length }})
          </mat-checkbox>
        }
      </app-labeled-field>

      <app-labeled-field #acceptsAsmDeleteField label="Accepts ASM Delete Records">
        <app-yes-no-toggle
          [formControl]="acceptsAsmDeleteControl"
          [ariaLabelledby]="acceptsAsmDeleteField.labelId"></app-yes-no-toggle>
      </app-labeled-field>
    }
  </app-form-section>

  <app-form-section heading="Incoming Files" description="CMS files this payer receives for the service year.">
    <app-setting-row #maoRow label="MAO-004" description="Encounter Data Report" [required]="true">
      <app-yes-no-toggle
        [formControl]="receivingMAOControl"
        [ariaLabelledby]="maoRow.labelId"
        (valueChanged)="onReceivingMAOChange($event)"></app-yes-no-toggle>
    </app-setting-row>

    <app-setting-row #morRow label="MOR" description="Model Output Report" [required]="true">
      <span class="mor-version-label">Version <span class="mor-version-label__required" aria-hidden="true">*</span></span>
      <app-filter-search-select
        #modelVersionSelect
        class="mor-version-select"
        [form]="form"
        [controlName]="modelVersionSelectControlName"
        [multiple]="true"
        [noneEnabled]="false"
        [required]="true"
        [compareWith]="compareById"
        [disabled]="form.get(receivingMORSelectControlName)?.value === 'No'"
        [label]="'Version'"
        [showLabel]="false"
        [noEntriesFoundLabel]="'No Versions Found'"></app-filter-search-select>
      <app-yes-no-toggle
        [formControl]="receivingMORControl"
        [ariaLabelledby]="morRow.labelId"
        (valueChanged)="onReceivingMORChange($event)"></app-yes-no-toggle>
    </app-setting-row>
  </app-form-section>

  <app-form-section heading="Notes" description="Optional context for your team." [columns]="1">
    <app-labeled-field label="Notes">
      <mat-form-field appearance="outline" color="primary">
        <textarea matInput [formControl]="notesControl" [rows]="3" aria-label="Notes"></textarea>
      </mat-form-field>
    </app-labeled-field>
  </app-form-section>
</mat-dialog-content>

<app-dialog-footer>
  <button mat-flat-button class="secondary-button" mat-dialog-close>Cancel</button>
  <button
    mat-flat-button
    class="primary-button-longtext"
    [disabled]="form.invalid"
    (click)="onSaveClick()">
    Save Policy
  </button>
</app-dialog-footer>
